import { Injectable } from '@angular/core';
import { TMachineMaintenanceState } from '../reducers';
import { Store, select } from '@ngrx/store';
import {
  TListingFetchReason,
  TNeedleRequestStatus,
  TRAISE_NEEDLE_REQUEST,
  TREQUEST_BULK_UPDATE_SPAREPART_REQUEST_STATUS,
  TREQUEST_NEEDLE_APPROVE,
  TREQUEST_NEEDLE_DELETE,
  TREQUEST_NEEDLE_LIST,
  TREQUEST_NEEDLE_UPDATE,
  TREQUEST_SPAREPART_REQUEST_LIST,
  TSparePartRequestStatus,
} from '@monorepo/types';
import * as NeedleManagementActions from './needle-management.actions';
import * as NeedleManagmentSelector from './needle-management.selectors';
import { TUserPaginationOption } from '@monorepo/ui';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { catchError, finalize, lastValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NeedleManagementFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private mmDataService: MachineMaintenanceDataService,
  ) {
    this.initializeNeedleListPagination();
    this.initializeNeedleRequestPagination();
    this.initializeLocalNeedleRequestPagination();
  }

  initializeNeedleListPagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('needleListPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setNeedleListPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('needleListPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setNeedleListPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setNeedleListPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('needleListPageSize', pagination.pageSize.toString());
    this.store.dispatch(new NeedleManagementActions.SetNeedleListPagination(pagination));
  }

  resetNeedleListInfo(fetchReason: TListingFetchReason) {
    const pageSize = Number(LOCAL_STORAGE_UTILS.retrieveData('needleListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0]);
    this.setNeedleListPagination({ pageNo: 1, pageSize });
    this.fetchNeedleListInfo(
      {
        selectedFilters: [],
      },
      fetchReason,
    );
  }

  fetchNeedleListInfo(payload: Omit<TREQUEST_NEEDLE_LIST, 'page' | 'itemsPerPage'>, fetchReason: TListingFetchReason) {
    this.store.dispatch(new NeedleManagementActions.GetNeedleListInfo(payload, fetchReason));
  }

  fetchNeedleListDropdown() {
    this.store.dispatch(new NeedleManagementActions.GetNeedleListDropdown());
  }

  deleteNeedleListRow(needleIds: TREQUEST_NEEDLE_DELETE['needleIds']) {
    this.store.dispatch(new NeedleManagementActions.DeleteNeedle(needleIds));
  }

  updateNeedleListRow(updatedNeedle: TREQUEST_NEEDLE_UPDATE) {
    this.store.dispatch(new NeedleManagementActions.UpdateNeedle(updatedNeedle));
  }

  raiseGlobalNeedleRequest(payload: TRAISE_NEEDLE_REQUEST) {
    this.store.dispatch(new NeedleManagementActions.RaiseNeedleRequest(payload));
  }

  async getBrandAndTypeByNeedleNumber(needleNumber: string) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getBrandAndTypeByNeedleNumber(needleNumber).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async searchNeedles(searchStr: string) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.searchNeedleNumbers(searchStr).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  initializeNeedleRequestPagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('needleRequestListPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setNeedleRequestPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('needleRequestListPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setNeedleRequestPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setNeedleRequestPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('needleRequestListPageSize', pagination.pageSize.toString());
    this.store.dispatch(new NeedleManagementActions.SetNeedleRequestPagination(pagination));
  }

  resetNeedleRequestList(fetchReason: TListingFetchReason, status?: TSparePartRequestStatus) {
    const pageSize = Number(
      LOCAL_STORAGE_UTILS.retrieveData('needleRequestListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0],
    );
    this.setNeedleRequestPagination({ pageNo: 1, pageSize });
    this.getNeedleRequestList(
      {
        sparePartRequestStatus: status || 'PENDING',
        selectedFilters: [],
      },
      fetchReason,
    );
  }

  getNeedleRequestList(
    payload: Omit<TREQUEST_SPAREPART_REQUEST_LIST, 'page' | 'itemsPerPage'>,
    fetchReason: TListingFetchReason,
  ) {
    this.store.dispatch(new NeedleManagementActions.GetNeedleRequestList(payload, fetchReason));
  }

  bulkUpdateNeedleRequest(payload: TREQUEST_BULK_UPDATE_SPAREPART_REQUEST_STATUS, status: TNeedleRequestStatus) {
    this.store.dispatch(new NeedleManagementActions.BulkUpdateNeedleRequest(payload, status));
  }

  approveNeedleRequest(payload: TREQUEST_NEEDLE_APPROVE, status: TNeedleRequestStatus) {
    this.store.dispatch(new NeedleManagementActions.ApproveNeedleRequest(payload, status));
  }

  initializeLocalNeedleRequestPagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('localNeedleRequestPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setLocalNeedleRequestPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('localNeedleRequestPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setLocalNeedleRequestPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setLocalNeedleRequestPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('localNeedleRequestPageSize', pagination.pageSize.toString());
    this.store.dispatch(new NeedleManagementActions.SetLocalNeedleRequestPagination(pagination));
  }

  resetLocalStoreRequests(fetchReason: TListingFetchReason, status?: TSparePartRequestStatus) {
    const pageSize = Number(LOCAL_STORAGE_UTILS.retrieveData('needleListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0]);
    this.setLocalNeedleRequestPagination({ pageNo: 1, pageSize });
    this.getLocalStoreRequests(
      {
        sparePartRequestStatus: status || 'PENDING',
        selectedFilters: [],
      },
      fetchReason,
    );
  }

  getLocalStoreRequests(
    payload: Omit<TREQUEST_SPAREPART_REQUEST_LIST, 'page' | 'itemsPerPage'>,
    fetchReason: TListingFetchReason,
  ) {
    this.store.dispatch(new NeedleManagementActions.GetLocalStoreRequests(payload, fetchReason));
  }

  getNeedleListDropdownInfo() {
    return this.store.pipe(select(NeedleManagmentSelector.getNeedleListDropdownInfo));
  }

  getNeedleListInfo() {
    return this.store.pipe(select(NeedleManagmentSelector.getNeedleListInfo));
  }

  getNeedleRequests() {
    return this.store.pipe(select(NeedleManagmentSelector.getNeedleRequests));
  }

  getLocalStoreNeedleRequests() {
    return this.store.pipe(select(NeedleManagmentSelector.getLocalStoreNeedleRequests));
  }

  getNeedleListPagination() {
    return this.store.pipe(select(NeedleManagmentSelector.getNeedleListPagination));
  }

  getNeedleRequestPagination() {
    return this.store.pipe(select(NeedleManagmentSelector.getNeedleRequestPagination));
  }

  getLocalNeedleRequestPagination() {
    return this.store.pipe(select(NeedleManagmentSelector.getLocalNeedleRequestPagination));
  }
}

export type TGetNeedleListInfo = ReturnType<NeedleManagementFacade['getNeedleListInfo']>;
export type TGetNeedleListDropdownInfo = ReturnType<NeedleManagementFacade['getNeedleListDropdownInfo']>;
export type TGetNeedleRequests = ReturnType<NeedleManagementFacade['getNeedleRequests']>;
export type TGetLocalStoreNeedleRequests = ReturnType<NeedleManagementFacade['getLocalStoreNeedleRequests']>;
export type TGetNeedleListPagination = ReturnType<NeedleManagementFacade['getNeedleListPagination']>;
export type TGetNeedleRequestPagination = ReturnType<NeedleManagementFacade['getNeedleRequestPagination']>;
export type TGetLocalNeedleRequestPagination = ReturnType<NeedleManagementFacade['getLocalNeedleRequestPagination']>;
